import { useState, useEffect, useCallback } from 'react';
import { Cpu, Save } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from './ui/toastContext';

interface LLMConfig {
  id: string;
  name: string;
  provider: string;
  base_url: string | null;
  model: string;
  temperature: number;
  max_tokens: number;
  is_global: boolean;
}

interface LlmConfigPanelProps {
  profileId: string;
  llmConfigId: string | null;
  onConfigChange: () => void;
}

const PROVIDERS = [
  'openai',
  'anthropic',
  'claude',
  'deepseek',
  'siliconflow',
  'moonshot',
  'zhipu',
  'dashscope',
  'volcengine',
  'jdcloud',
  'exgate',
  'custom',
];

// 从 axios 错误中提取后端 detail 文案
function apiErrorDetail(e: unknown): string | null {
  const err = e as { response?: { data?: { detail?: unknown } } };
  return typeof err.response?.data?.detail === 'string' ? err.response.data.detail : null;
}

export default function LlmConfigPanel({ profileId, llmConfigId, onConfigChange }: LlmConfigPanelProps) {
  const [config, setConfig] = useState<LLMConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const toast = useToast();

  // Form fields
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o');
  const [baseUrl, setBaseUrl] = useState('');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(4096);
  const [apiKey, setApiKey] = useState('');

  const loadConfig = useCallback(async () => {
    if (!llmConfigId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/llm-configs/${llmConfigId}`);
      const data = res.data as LLMConfig;
      setConfig(data);
      setName(data.name);
      setProvider(data.provider);
      setModel(data.model);
      setBaseUrl(data.base_url || '');
      setTemperature(data.temperature);
      setMaxTokens(data.max_tokens);
      setFormError(null);
    } catch (e) {
      console.error('Failed to load LLM config:', e);
    } finally {
      setLoading(false);
    }
  }, [llmConfigId]);

  // 渲染期调整：llmConfigId 被清空时同步重置表单（替代原 useEffect 的 else 分支）
  const [prevLlmConfigId, setPrevLlmConfigId] = useState(llmConfigId);
  if (prevLlmConfigId !== llmConfigId) {
    setPrevLlmConfigId(llmConfigId);
    if (!llmConfigId) {
      setConfig(null);
      setName('');
      setProvider('openai');
      setModel('gpt-4o');
      setBaseUrl('');
      setTemperature(0.7);
      setMaxTokens(4096);
      setApiKey('');
      setFormError(null);
    }
  }

  useEffect(() => {
    if (!llmConfigId) return;
    void (async () => {
      await loadConfig();
    })();
  }, [llmConfigId, loadConfig]);

  const handleSave = async () => {
    if (!name.trim() || !model.trim()) {
      setFormError('名称和模型为必填');
      return;
    }
    if (!apiKey.trim()) {
      setFormError('API Key 为必填');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        provider,
        model: model.trim(),
        api_key: apiKey.trim(),
        base_url: baseUrl.trim() || null,
        temperature,
        max_tokens: maxTokens,
        is_global: false,
      };
      if (config) {
        await apiClient.put(`/llm-configs/${config.id}`, payload);
        await loadConfig();
      } else {
        const res = await apiClient.post('/llm-configs', payload);
        const newConfig = res.data as LLMConfig;
        // Update profile's llm_config_id
        await apiClient.put(`/profiles/${profileId}`, { llm_config_id: newConfig.id });
        onConfigChange();
      }
      toast.success(config ? 'LLM 配置已更新' : 'LLM 配置已创建并关联到当前 Profile');
      setFormError(null);
    } catch (e) {
      const detail = apiErrorDetail(e);
      setFormError(detail || '保存失败');
      toast.error('保存失败: ' + (detail || (e instanceof Error ? e.message : '未知错误')));
    } finally {
      setSaving(false);
    }
  };

  const renderSlider = (
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    onChange: (v: number) => void
  ) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-muted">{label}</label>
        <span style={{ fontSize: 13, color: 'var(--color-primary)', fontWeight: 500 }}>{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--color-primary)' }}
      />
      <div className="flex items-center justify-between text-muted" style={{ fontSize: 11, marginTop: 2 }}>
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Cpu size={16} />
        <h3 style={{ margin: 0 }}>LLM 配置</h3>
      </div>

      {loading ? (
        <div className="loading" style={{ padding: 20 }}>
          <div className="spinner" />
        </div>
      ) : (
        <div className="card" style={{ background: 'var(--bg-primary)' }}>
          {formError && (
            <div className="card mb-4" style={{ borderColor: 'var(--color-error)', background: 'rgba(248,81,73,0.1)' }}>
              <p style={{ color: 'var(--color-error)', margin: 0, fontSize: 13 }}>{formError}</p>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                配置名称 <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="如：OpenAI GPT-4o" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                  Provider <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <select value={provider} onChange={(e) => setProvider(e.target.value)}>
                  {PROVIDERS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                  模型 <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="gpt-4o" />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                Base URL <span className="text-muted" style={{ fontSize: 11 }}>(可选)</span>
              </label>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://api.openai.com/v1"
              />
            </div>

            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                API Key <span style={{ color: 'var(--color-error)' }}>*</span>
                {config && <span className="text-muted" style={{ fontSize: 11, marginLeft: 4 }}>(加密存储，编辑需重新输入)</span>}
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
              />
            </div>

            {renderSlider('Temperature', temperature, 0, 2, 0.1, setTemperature)}

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-muted">Max Tokens</label>
                <input
                  type="number"
                  min={1}
                  max={128000}
                  step={1}
                  value={maxTokens}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v)) setMaxTokens(Math.max(1, Math.min(128000, v)));
                  }}
                  style={{ width: 100, fontSize: 13, textAlign: 'right' }}
                />
              </div>
              <input
                type="range"
                min={256}
                max={32768}
                step={256}
                value={Math.min(maxTokens, 32768)}
                onChange={(e) => setMaxTokens(parseInt(e.target.value, 10))}
                style={{ width: '100%', accentColor: 'var(--color-primary)' }}
              />
              <div className="flex items-center justify-between text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                <span>256</span>
                <span>128000</span>
              </div>
            </div>

            <button
              className="primary mt-2"
              onClick={handleSave}
              disabled={saving}
              style={{ width: '100%' }}
            >
              <Save size={14} /> {saving ? '保存中...' : config ? '更新配置' : '创建配置'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
